import { createContext, useContext, useEffect, useState } from "react";
import { getProfile, setCurrentUserUid } from "../lib/api";
import { logger } from "../lib/logger";

const SESSION_KEY = "hub_session";
const SESSION_TTL = 30 * 24 * 60 * 60 * 1000;

function log(...args) { logger.info("[Auth]", ...args); }
function logErr(...args) { logger.error("[Auth]", ...args); }

const AuthCtx = createContext(null);

// Centralized role definitions
export const ROLES = {
  ADMIN: "admin",
  DEVELOPER: "developer",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Derived state for easy role checking
  const userRole = (user?.role || "").trim().toLowerCase();
  const isAdmin = userRole === ROLES.ADMIN;
  const isDev = userRole === ROLES.DEVELOPER;

  useEffect(() => {
    log("=== INIT SESSION ===");
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const { user: storedUser, expiresAt } = JSON.parse(stored);
        log("Found stored session for:", storedUser?.email, "role:", storedUser?.role);
        if (expiresAt && Date.now() > expiresAt) {
          log("Session expired, clearing");
          localStorage.removeItem(SESSION_KEY);
          setLoading(false);
          return;
        }
        log("Session valid, restoring user");
        setUser(storedUser);
        setCurrentUserUid(storedUser.uid);
        fetchProfile(storedUser.uid);
      } catch (e) {
        logErr("Corrupt session, clearing:", e.message);
        localStorage.removeItem(SESSION_KEY);
      }
    } else {
      log("No stored session");
    }
    setLoading(false);
  }, []);

  async function fetchProfile(userId) {
    if (!userId) return;
    log("Fetching profile for user:", userId);
    try {
      const p = await getProfile(userId);
      log("Profile loaded:", p ? "yes" : "no");
      setProfile(p);
    } catch (e) {
      logErr("Error fetching profile:", e.message);
    }
  }

  async function login(email, password) {
    log("=== LOGIN ATTEMPT === email:", email);
    const res = await fetch("/api/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    log("Login response status:", res.status);
    const data = await res.json();
    if (!res.ok) {
      logErr("Login failed:", data.error);
      throw new Error(data.error || "Credenciais inválidas");
    }

    const safeUser = data.user;
    log("Login successful, user:", safeUser.email, "role:", safeUser.role);
    setUser(safeUser);
    setCurrentUserUid(safeUser.uid);
    localStorage.setItem(SESSION_KEY, JSON.stringify({ user: safeUser, expiresAt: Date.now() + SESSION_TTL }));
    log("Session saved to localStorage");
    await fetchProfile(safeUser.uid);
    return safeUser;
  }

  function logout() {
    log("=== LOGOUT === user:", user?.email);
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setCurrentUserUid(null);
    setProfile(null);
    log("Session cleared");
  }

  const refreshProfile = () => {
    log("Manual profile refresh requested");
    fetchProfile(user?.uid);
  };

  async function updateUserState(newData) {
    log("Updating user state, keys:", Object.keys(newData));
    const updated = { ...user, ...newData };
    setUser(updated);
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        parsed.user = updated;
        localStorage.setItem(SESSION_KEY, JSON.stringify(parsed));
        log("User state persisted to localStorage");
      } catch (e) {
        logErr("Failed to persist user state:", e.message);
      }
    }
    await fetchProfile(user?.uid);
  }

  return (
    <AuthCtx.Provider value={{ user, profile, loading, login, logout, refreshProfile, updateUserState, isAdmin, isDev }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
